import { useCallback, useLayoutEffect, useRef } from 'react';

interface RowFieldSetter<T> {
  /** Sets `receipts.<current index>.<path>`; a no-op once the row is gone. */
  setRowField: (path: string, value: unknown) => void;
  /** The row as of the last render, or undefined once the row is gone. */
  getRow: () => T | undefined;
}

/**
 * Writes fields of one receipt batch row from async callbacks (uploads,
 * receipt analysis). Those resolve after the row may have moved: deleting a
 * row above shifts every index below it, so the index captured when the
 * request started would hit a neighbouring row. Writes go through Formik's
 * `setFieldValue`, which applies to the live form state, so writes landing
 * for many rows in the same tick compose instead of overwriting each other
 * the way `setValues(prev => ...)` does.
 */
export function useRowFieldSetter<T>(
  index: number,
  row: T,
  setFieldValue: (field: string, value: unknown) => unknown
): RowFieldSetter<T> {
  const latest = useRef<{ index: number; row: T } | undefined>({ index, row });

  useLayoutEffect(() => {
    latest.current = { index, row };
  }, [index, row]);

  useLayoutEffect(
    () => () => {
      latest.current = undefined;
    },
    []
  );

  const setRowField = useCallback(
    (path: string, value: unknown) => {
      if (latest.current === undefined) {
        return;
      }
      void setFieldValue(`receipts.${latest.current.index}.${path}`, value);
    },
    [setFieldValue]
  );

  const getRow = useCallback(() => latest.current?.row, []);

  return { setRowField, getRow };
}
